// OAuth Meta para Contas Sociais / Marketing.
// Path sem "ads" no nome do arquivo — ad blockers bloqueiam URLs com /ads/.

export const META_OAUTH_VERSION = 'v25.0';
export const MKT_STATE_PREFIX = 'mkt_';
export const MKT_OAUTH_RETURN_KEY = 'mkt_oauth_return';

// Grupos de escopo por demanda do usuario. Cada entrada do dropdown "Conectar
// Conta" pede so o grupo correspondente; permissoes na Meta sao cumulativas
// por usuario+app, entao pedir um grupo novo nao revoga os anteriores.
//
// ATENCAO: escopo nao habilitado em App Review > Permissions and Features
// derruba o dialogo INTEIRO com "Invalid Scopes". Confirme no App Dashboard
// antes de alterar estas listas.

// ── App Facebook Login (1553356899550775) ────────────────────────────────

// Conta Social facebook vinculada a canal Messenger herda o page token DAQUI
// (vincularContaSocial) — pages_messaging + pages_utility_messaging precisam
// estar neste grupo ou o canal nasce sem permissao de DM/templates.
export const ESCOPOS_MESSENGER = [
    'pages_show_list',
    'business_management',
    // pages_messaging: obrigatorio para enviar/receber DM no Messenger. Sem
    // este escopo a Graph responde (#230) "Requires pages_messaging
    // permission" em todo envio de texto.
    'pages_messaging',
    // pages_utility_messaging: templates de utility message no mesmo vinculo.
    // ATENCAO ao nome: e "pages_" (plural). A doc da Meta escreve
    // "page_utility_messaging" (singular), que nao existe e derruba o dialogo.
    'pages_utility_messaging',
    'pages_manage_metadata',
    'pages_read_engagement',
];

// Gerenciamento de pagina: posts, comentarios e metricas.
export const ESCOPOS_FACEBOOK = [
    'pages_show_list',
    'business_management',
    'pages_read_engagement',
    'pages_manage_posts',
    'pages_manage_metadata',
    // Listar posts da Pagina (GET /{page-id}/posts) para o backfill de
    // metaComments.service.js — sem ele a Graph responde (#10) "requires the
    // 'pages_read_user_content' permission". Habilitada no App Dashboard.
    'pages_read_user_content',
    // Responder/moderar comentarios de terceiros.
    'pages_manage_engagement',
    // Page Insights (GET /{page-id}/insights) — alimenta o painel de metricas
    // do perfil da Pagina. AINDA pendente de App Review: esta tambem em
    // ESCOPOS_NAO_HABILITADOS_NO_APP logo abaixo, e abrirMetaOAuth filtra de
    // toda requisicao. Fica declarado aqui para que, quando a Meta aprovar,
    // baste remover da lista de pendentes.
    'read_insights',
];

export const ESCOPOS_ANUNCIOS = [
    // pages_show_list: sem ele /me/accounts volta vazio e o webhook devolve
    // meta=sem_paginas; tambem alimenta promote_pages das contas de anuncio.
    'pages_show_list',
    'business_management',
    'ads_read',
    'pages_manage_ads',
];

// ── App Instagram Login (1634440460959057) ───────────────────────────────

export const ESCOPOS_DIRECT_INSTAGRAM = [
    'instagram_business_basic',
    'instagram_business_manage_messages',
];

// Publicacao, comentarios e metricas do perfil.
export const ESCOPOS_INSTAGRAM = [
    'instagram_business_basic',
    'instagram_business_content_publish',
    'instagram_business_manage_comments',
    // instagram_business_manage_insights: exigido pelos endpoints de insights
    // que alimentam Marketing -> Social Analytics.
    'instagram_business_manage_insights',
];

/** Uniao com dedupe, preservando a ordem de chegada. */
export function unirEscopos(...listas) {
    return [...new Set(listas.flat().filter(Boolean))];
}

export const ESCOPOS_FACEBOOK_TODOS = unirEscopos(ESCOPOS_MESSENGER, ESCOPOS_FACEBOOK, ESCOPOS_ANUNCIOS);
export const ESCOPOS_INSTAGRAM_TODOS = unirEscopos(ESCOPOS_DIRECT_INSTAGRAM, ESCOPOS_INSTAGRAM);

// Escopos ja presentes nos grupos acima mas AINDA NAO habilitados em App
// Review do app Facebook — pedir um deles no dialogo derruba tudo com
// "Invalid Scopes". abrirMetaOAuth filtra esta lista de toda requisicao;
// quando a Meta habilitar, basta tirar da lista (ver pages_manage_engagement,
// que saiu daqui quando o App Dashboard aprovou a permissao).
export const ESCOPOS_NAO_HABILITADOS_NO_APP = [
    // Sem ele, todo grupo de /insights da Pagina falha com MISSING_SCOPE e o
    // painel de metricas do perfil mostra so os campos de no. Enviar antes da
    // aprovacao derrubaria o dialogo inteiro com "Invalid Scopes", quebrando
    // ate a conexao de contas novas.
    'read_insights',
];

/**
 * Escopos ja concedidos nas contas existentes de um app, para reenviar na
 * proxima autorizacao: rerequest com subconjunto nao revoga nada, mas enviar
 * a uniao garante que escopos negados sejam reexibidos e que o proximo
 * debug_token nao "perca" nenhum concedido.
 *
 * Filtra pela whitelist de escopos conhecidos do app: account.scopes pode
 * trazer escopos legados do debug_token (instagram_basic, public_profile...)
 * que, reenviados num app onde nao estao habilitados, derrubam o dialogo.
 *
 * @param {Array} accounts — lista de contas do mktAccountsService
 * @param {'facebook'|'instagram'} app
 */
export function escoposConcedidos(accounts, app) {
    const doApp = (accounts || []).filter((a) => (
        app === 'instagram'
            ? a.provider === 'instagram' && a.account_info?.instagram_login === true
            : a.provider === 'facebook' || a.provider === 'meta_ads'
    ));
    const conhecidos = app === 'instagram' ? ESCOPOS_INSTAGRAM_TODOS : ESCOPOS_FACEBOOK_TODOS;
    return unirEscopos(doApp.map((a) => a.scopes || []))
        .filter((s) => conhecidos.includes(s));
}

/**
 * Abre o dialogo OAuth do Facebook Login pedindo os escopos informados.
 *
 * auth_type=rerequest: obriga a Meta a reexibir permissoes recusadas/novas —
 * sem isso, quem ja conectou antes nunca recebe os escopos adicionais.
 *
 * @param {string} clienteId
 * @param {{ scopes?: string[], returnTo?: string }} [opts]
 *   scopes ausente → ESCOPOS_FACEBOOK_TODOS (comportamento antigo);
 *   returnTo → rota para voltar apos o redirect do webhook
 */
export function abrirMetaOAuth(clienteId, opts = {}) {
    const appId = import.meta.env.VITE_FACEBOOK_APP_ID || '1553356899550775';
    const redirectUri = import.meta.env.VITE_META_AUTH_REDIRECT_URI
        || 'https://webhook-beta.lais.app/webhook/v1/auth';

    if (opts.returnTo) {
        try {
            sessionStorage.setItem(MKT_OAUTH_RETURN_KEY, opts.returnTo);
        } catch (_) { /* private mode */ }
    }

    const scope = unirEscopos(opts.scopes || ESCOPOS_FACEBOOK_TODOS)
        .filter((s) => !ESCOPOS_NAO_HABILITADOS_NO_APP.includes(s))
        .join(',');
    const url = `https://www.facebook.com/${META_OAUTH_VERSION}/dialog/oauth`
        + `?client_id=${encodeURIComponent(appId)}`
        + `&state=${encodeURIComponent(`${MKT_STATE_PREFIX}${clienteId}`)}`
        + `&redirect_uri=${encodeURIComponent(redirectUri)}`
        + `&scope=${encodeURIComponent(scope)}`
        + `&response_type=code`
        + `&auth_type=rerequest`;

    window.location.assign(url);
}

/**
 * Instagram Login for Business — app proprio (nao cobre ads_read /
 * pages_manage_ads). O webhook troca o code em graph.instagram.com e cria a
 * conta com account_info.instagram_login = true.
 *
 * @param {string} clienteId
 * @param {{ scopes?: string[] }} [opts] — scopes ausente → ESCOPOS_INSTAGRAM_TODOS
 */
export function abrirInstagramOAuth(clienteId, opts = {}) {
    const appId = import.meta.env.VITE_INSTAGRAM_APP_ID || '1634440460959057';
    const redirectUri = import.meta.env.VITE_INSTAGRAM_AUTH_REDIRECT_URI
        || (import.meta.env.VITE_META_AUTH_REDIRECT_URI || '').replace('/webhook/v1/auth', '/webhook/v1/auth/instagram')
        || 'https://webhook-beta.lais.app/webhook/v1/auth/instagram';
    const scope = unirEscopos(opts.scopes || ESCOPOS_INSTAGRAM_TODOS).join(',');
    const url = `https://www.instagram.com/oauth/authorize`
        + `?force_reauth=true`
        + `&client_id=${appId}`
        + `&state=${MKT_STATE_PREFIX}${clienteId}`
        + `&redirect_uri=${encodeURIComponent(redirectUri)}`
        + `&scope=${encodeURIComponent(scope)}`
        + `&response_type=code`;
    window.location.assign(url);
}

export function consumeOAuthReturnPath() {
    try {
        const path = sessionStorage.getItem(MKT_OAUTH_RETURN_KEY);
        sessionStorage.removeItem(MKT_OAUTH_RETURN_KEY);
        return path || null;
    } catch (_) {
        return null;
    }
}
